import { useCallback, useRef, useState } from "react";

export default function useEditorHistory({
  objects,
  setObjects,
  selectedIds,
  setSelectedIds,
  setShowSelEditor,
  newId,
}) {
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const clipboardRef = useRef([]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setRedoStack([]);
  }, []);

  function pushHist(objs) {
    setHistory(h => [...h.slice(-29), objs]);
    setRedoStack([]);
  }

  function undo() {
    setHistory(h => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setRedoStack(r => [...r, objects]);
      setObjects(prev);
      setSelectedIds([]);
      return h.slice(0, -1);
    });
  }

  function redo() {
    setRedoStack(r => {
      if (!r.length) return r;
      const next = r[r.length - 1];
      setHistory(h => [...h, objects]);
      setObjects(next);
      return r.slice(0, -1);
    });
  }

  function cloneObj(o) {
    const c = { ...o };
    if (o.points) c.points = o.points.map(p => ({ ...p }));
    if (o.lines) c.lines = [...o.lines];
    return c;
  }

  function deleteSelected() {
    if (!selectedIds.length) return;
    pushHist(objects);
    setObjects(os => os.filter(o => !selectedIds.includes(o.id)));
    setSelectedIds([]);
    setShowSelEditor(false);
  }

  function duplicateObjects(ids) {
    const sels = objects.filter(o => ids.includes(o.id));
    if (!sels.length) return [];

    const copies = sels.map(o => ({
      ...cloneObj(o),
      id: newId(),
      x: (o.x || 0) + 20,
      y: (o.y || 0) + 20,
      x1: (o.x1 || 0) + 20,
      y1: (o.y1 || 0) + 20,
      x2: (o.x2 || 0) + 20,
      y2: (o.y2 || 0) + 20,
      anchorX: o.anchorX == null ? o.anchorX : o.anchorX + 20,
      anchorY: o.anchorY == null ? o.anchorY : o.anchorY + 20,
      targetX: o.targetX == null ? o.targetX : o.targetX + 20,
      targetY: o.targetY == null ? o.targetY : o.targetY + 20,
      lensX: o.lensX == null ? o.lensX : o.lensX + 20,
      lensY: o.lensY == null ? o.lensY : o.lensY + 20,
      points: o.points?.map(p => ({ x: p.x + 20, y: p.y + 20 })),
    }));

    pushHist(objects);
    setObjects(os => [...os, ...copies]);
    setSelectedIds(copies.map(c => c.id));
    return copies;
  }

  function clipboardCopy() {
    if (!selectedIds.length) return;
    clipboardRef.current = objects.filter(o => selectedIds.includes(o.id)).map(cloneObj);
  }

  function clipboardPaste() {
    if (!clipboardRef.current.length) return;

    const copies = clipboardRef.current.map(o => ({
      ...cloneObj(o),
      id: newId(),
      x: (o.x || 0) + 20,
      y: (o.y || 0) + 20,
      x1: (o.x1 || 0) + 20,
      y1: (o.y1 || 0) + 20,
      x2: (o.x2 || 0) + 20,
      y2: (o.y2 || 0) + 20,
      anchorX: o.anchorX == null ? o.anchorX : o.anchorX + 20,
      anchorY: o.anchorY == null ? o.anchorY : o.anchorY + 20,
      targetX: o.targetX == null ? o.targetX : o.targetX + 20,
      targetY: o.targetY == null ? o.targetY : o.targetY + 20,
      lensX: o.lensX == null ? o.lensX : o.lensX + 20,
      lensY: o.lensY == null ? o.lensY : o.lensY + 20,
      points: o.points?.map(p => ({ x: p.x + 20, y: p.y + 20 })),
    }));

    clipboardRef.current = copies.map(cloneObj);
    pushHist(objects);
    setObjects(os => [...os, ...copies]);
    setSelectedIds(copies.map(c => c.id));
  }

  return {
    history,
    redoStack,
    clearHistory,
    pushHist,
    undo,
    redo,
    cloneObj,
    deleteSelected,
    duplicateObjects,
    clipboardCopy,
    clipboardPaste,
  };
}
